/**
 * Role-first canvas load (FUSION-CANVAS01).
 *
 * Context nodes are kept in the data layer and left off the Cytoscape instance
 * until the officer asks for them. The first ELK pass therefore sees the
 * role-bearing set. This is the Fusion canvas (`window.__fusionCy`). The
 * Workbench `canvasController` stashes the investigation graph (`window.__cy`
 * and `useGraphStore`); writing a case's context rows into that store would
 * fill the workbench session stash. The reason tag is still `context`, and
 * restore is one layout after the whole set is back, not a layout per node.
 */
import type cytoscape from 'cytoscape';
import type { ElementDefinition } from 'cytoscape';

export const FUSION_CANVAS_ROLE_FIRST_MIN_NODES = 400;
export const FUSION_CANVAS_HARD_CAP = 5000;

const FLOW_ROLES = new Set(['subject', 'source', 'sink', 'relay', 'shared_relay']);
const KEEP_TYPES = new Set(['phase_lane', 'group_box']);

export type RoleFirstMode = 'passthrough' | 'role-first' | 'hard-cap';

export interface RoleFirstPlan {
  mode: RoleFirstMode;
  /** Elements that enter the canvas before the first layout. */
  layout: ElementDefinition[];
  /** Context elements held off the canvas. Empty in hard-cap mode (not loaded). */
  held: ElementDefinition[];
  contextCount: number;
  reason: 'context';
}

function readSetting(name: string, fallback: number): number {
  const env = import.meta.env as Record<string, string | undefined>;
  const raw = env[`VITE_${name}`] ?? env[name];
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : fallback;
}

export function roleFirstMinNodes(): number {
  return readSetting('FUSION_CANVAS_ROLE_FIRST_MIN_NODES', FUSION_CANVAS_ROLE_FIRST_MIN_NODES);
}

export function roleFirstHardCap(): number {
  return readSetting('FUSION_CANVAS_HARD_CAP', FUSION_CANVAS_HARD_CAP);
}

function dataOf(el: ElementDefinition): Record<string, unknown> {
  return (el.data || {}) as Record<string, unknown>;
}

function isEdge(el: ElementDefinition): boolean {
  const d = dataOf(el);
  return d.source != null || d.target != null;
}

function idOf(el: ElementDefinition): string {
  return String(dataOf(el).id ?? '');
}

/**
 * Split a graph so the first layout skips context.
 * Below `minNodes` the input is returned unchanged.
 * Above `hardCap` the context set is not loaded (no toggle).
 */
export function partitionRoleFirst(
  elements: ElementDefinition[],
  opts?: { minNodes?: number; hardCap?: number },
): RoleFirstPlan {
  const minNodes = opts?.minNodes ?? roleFirstMinNodes();
  const hardCap = opts?.hardCap ?? roleFirstHardCap();
  const nodes = elements.filter((el) => !isEdge(el));
  const edges = elements.filter(isEdge);
  const passthrough: RoleFirstPlan = {
    mode: 'passthrough', layout: elements, held: [], contextCount: 0, reason: 'context',
  };
  if (nodes.length < minNodes) return passthrough;

  const parentIds = new Set<string>();
  for (const n of nodes) {
    const p = dataOf(n).parent;
    if (typeof p === 'string' && p) parentIds.add(p);
  }
  const neighbors = new Map<string, Set<string>>();
  const link = (a: string, b: string) => {
    if (!a || !b) return;
    let bag = neighbors.get(a);
    if (!bag) { bag = new Set(); neighbors.set(a, bag); }
    bag.add(b);
  };
  for (const e of edges) {
    const d = dataOf(e);
    // LEDGER01: the lowest tier decides no node's place (the backend's spine_partition skips it too)
    if (String(d.edge_type ?? '') === 'ledger') continue;
    link(String(d.source ?? ''), String(d.target ?? ''));
    link(String(d.target ?? ''), String(d.source ?? ''));
  }

  const flow = new Set<string>();
  const context = new Set<string>();
  const pending = new Set<string>();
  for (const n of nodes) {
    const id = idOf(n);
    const d = dataOf(n);
    const type = String(d.type || '');
    const role = String(d.placement_role || '').trim();
    if (parentIds.has(id) || KEEP_TYPES.has(type)) {
      flow.add(id);
      continue;
    }
    if (FLOW_ROLES.has(role)) flow.add(id);
    else if (role === 'context') context.add(id);
    else if (!role) pending.add(id);
    else flow.add(id);
  }

  let moved = true;
  while (moved) {
    moved = false;
    for (const id of [...pending]) {
      const neigh = neighbors.get(id);
      const touchesFlow = !!neigh && [...neigh].some((nid) => flow.has(nid));
      if (touchesFlow) {
        flow.add(id);
        pending.delete(id);
        moved = true;
        continue;
      }
      const touchesPending = !!neigh && [...neigh].some((nid) => pending.has(nid));
      const onlyContext = !neigh || [...neigh].every((nid) => context.has(nid));
      if (onlyContext && !touchesPending) {
        context.add(id);
        pending.delete(id);
        moved = true;
      }
    }
  }
  for (const id of pending) context.add(id);

  if (context.size === 0) return passthrough;

  const hard = nodes.length > hardCap;
  const layoutIds = new Set(nodes.filter((n) => !context.has(idOf(n))).map(idOf));
  const edgeStays = (e: ElementDefinition) => {
    const d = dataOf(e);
    return layoutIds.has(String(d.source ?? '')) && layoutIds.has(String(d.target ?? ''));
  };
  return {
    mode: hard ? 'hard-cap' : 'role-first',
    layout: [...nodes.filter((n) => layoutIds.has(idOf(n))), ...edges.filter(edgeStays)],
    held: hard ? [] : [...nodes.filter((n) => context.has(idOf(n))), ...edges.filter((e) => !edgeStays(e))],
    contextCount: context.size,
    reason: 'context',
  };
}

export function contextChipLabel(plan: RoleFirstPlan, revealed: boolean): string | null {
  if (plan.mode === 'passthrough') return null;
  const n = plan.contextCount.toLocaleString('en-US');
  if (plan.mode === 'hard-cap') {
    return `context not loaded (${n} nodes); open the report for the full graph`;
  }
  return revealed ? `context: ${n} shown` : `context: ${n} hidden`;
}

/**
 * Put the held set onto the canvas, or take it off. Calls `layoutOnce` a single
 * time after the whole set has moved.
 */
export function toggleContextHold(
  cy: cytoscape.Core,
  held: ElementDefinition[],
  showing: boolean,
  layoutOnce: (cy: cytoscape.Core) => void,
): boolean {
  if (showing) {
    cy.batch(() => {
      for (const el of held) {
        const node = cy.getElementById(idOf(el));
        if (node.nonempty()) node.remove();
      }
    });
    layoutOnce(cy);
    return false;
  }
  const nodes = held.filter((el) => !isEdge(el));
  const edges = held.filter(isEdge);
  cy.batch(() => {
    for (const el of nodes) {
      if (cy.getElementById(idOf(el)).empty()) cy.add(el);
    }
    for (const el of edges) {
      if (cy.getElementById(idOf(el)).empty()) {
        try { cy.add(el); } catch { /* endpoint not on the canvas */ }
      }
    }
  });
  layoutOnce(cy);
  return true;
}
